import { Suspense, useMemo, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import useDeviceQuality from './useDeviceQuality';

const fmtQ = (n) => `${new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 }).format(n || 0)} q`;

function Bar({ x, value, maxV, color, delay }) {
  const ref = useRef();
  const t0 = useRef(performance.now());
  const target = 0.2 + 1.2 * (value / (maxV || 1));
  useFrame(() => {
    const t = Math.min(1, Math.max(0, (performance.now() - t0.current - delay) / 1400));
    const e = 1 - Math.pow(1 - t, 4);
    const h = Math.max(0.001, target * e);
    if (ref.current) { ref.current.scale.y = h; ref.current.position.y = 0.06 + h / 2; }
  });
  return (
    <group position={[x, 0, 0]}>
      <mesh ref={ref}>
        <boxGeometry args={[0.36, 1, 0.36]}/>
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.55} transparent opacity={0.88} roughness={0.3}/>
      </mesh>
    </group>
  );
}

function Orchard({ count }) {
  const ref = useRef();
  const matrices = useMemo(() => {
    const m = new THREE.Matrix4();
    const out = [];
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2 * 7.3;
      const r = 0.95 + (i % 5) * 0.12;
      const s = 0.6 + ((i * 37) % 10) / 20;
      m.compose(new THREE.Vector3(Math.cos(a) * r, 0.14 * s, Math.sin(a) * r), new THREE.Quaternion(), new THREE.Vector3(s, s, s));
      out.push(m.clone());
    }
    return out;
  }, [count]);
  return (
    <instancedMesh ref={(n) => { if (n) { matrices.forEach((mm, i) => n.setMatrixAt(i, mm)); n.instanceMatrix.needsUpdate = true; } ref.current = n; }}
                   args={[null, null, count]}>
      <coneGeometry args={[0.06, 0.2, 7]}/>
      <meshStandardMaterial color="#5f8f2a" roughness={0.8}/>
    </instancedMesh>
  );
}

// Visualizzatore 3D della stima: appezzamento con frutteto e colonne min / stima / max.
export default function YieldViz3D({ min, mid, max }) {
  const quality = useDeviceQuality();
  const maxV = Math.max(min || 0, mid || 0, max || 0);
  return (
    <div className="relative h-[300px] w-full hero3d-grab" data-testid="yield-3d-viz"
         style={{ background: 'radial-gradient(ellipse at 50% 70%, #142010 0%, #070a08 70%)' }}>
      <Canvas dpr={quality.dpr} resize={{ offsetSize: true }} camera={{ position: [2.9, 2.2, 3.5], fov: 38 }} gl={{ antialias: !quality.lite, alpha: true }}>
        <Suspense fallback={null}>
          <ambientLight intensity={0.45}/>
          <directionalLight position={[3, 5, 2]} intensity={1.6} color="#fff2d4"/>
          <pointLight position={[0, 1.2, 0]} intensity={1.2} color="#b4ff00" distance={4}/>
          <mesh position={[0, 0, 0]}>
            <cylinderGeometry args={[1.7, 1.78, 0.12, 64]}/>
            <meshStandardMaterial color="#1d2e14" roughness={0.95}/>
          </mesh>
          <mesh position={[0, 0.062, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <ringGeometry args={[1.62, 1.7, 64]}/>
            <meshBasicMaterial color="#b4ff00" transparent opacity={0.5}/>
          </mesh>
          <Orchard count={quality.lite ? 40 : 90}/>
          <Bar x={-0.55} value={min} maxV={maxV} color="#8dd8ff" delay={0}/>
          <Bar x={0} value={mid} maxV={maxV} color="#b4ff00" delay={180}/>
          <Bar x={0.55} value={max} maxV={maxV} color="#ff9b5e" delay={360}/>
          <OrbitControls enablePan={false} enableZoom={false} autoRotate autoRotateSpeed={0.8}
                         minPolarAngle={Math.PI * 0.2} maxPolarAngle={Math.PI * 0.45} target={[0, 0.75, 0]}/>
        </Suspense>
      </Canvas>
      <div className="absolute top-3 left-4 text-[9px] uppercase tracking-[0.28em] font-bold text-white/55 pointer-events-none">
        Visualizzazione 3D della resa
      </div>
      <div className="absolute bottom-3 inset-x-3 flex justify-center gap-2 pointer-events-none" data-testid="yield-3d-legend">
        {[['Minimo', min, '#8dd8ff'], ['Stima', mid, '#b4ff00'], ['Massimo', max, '#ff9b5e']].map(([l, v, c]) => (
          <div key={l} className="rounded-xl px-3 py-1.5 text-center" style={{ background: 'rgba(5,8,10,0.6)', border: `1px solid ${c}55`, backdropFilter: 'blur(10px)' }}>
            <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-white/60">{l}</div>
            <div className="text-sm font-bold" style={{ color: c }}>{fmtQ(v)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
